import { useState, useEffect, useCallback } from "react";

import { buildUrl } from "neetocommons/utils";
import { Label, Typography, Input, Switch, Button } from "neetoui";
import { assoc, not } from "ramda";
import { useTranslation } from "react-i18next";

import CopyToClipboardButton from "components/CopyToClipboardButton";

import {
  formatTime,
  parseTime,
  shouldRenderHoursInput,
  validateInput,
} from "./utils";

const SharePaneBody = ({
  recordingUrls,
  totalDuration,
  getCurrentTimestamp,
  isOpen,
  onClose,
  preview: Preview,
  enableAutoplayAndMuteFlags,
}) => {
  const { t } = useTranslation();

  const currentTimestamp = getCurrentTimestamp();

  const [shareLinks, setShareLinks] = useState([]);
  const [currentPlaybackTime, setCurrentPlaybackTime] =
    useState(currentTimestamp);

  const [params, setParams] = useState({
    muted: false,
    autoplay: false,
  });

  const { muted, autoplay } = params;

  const [rawInput, setRawInput] = useState("");

  const showHours = shouldRenderHoursInput(totalDuration);

  const handlePlaybackTimestampChange = e => {
    const input = e.target.value;
    if (validateInput(input)) {
      setRawInput(input);
    }
  };

  const handleFlagsChange = (key, value) => setParams(assoc(key, value));

  const formatAndSetTime = () => {
    let timeInSeconds = parseTime(rawInput);
    if (timeInSeconds > totalDuration) {
      timeInSeconds = totalDuration;
    }
    setRawInput(formatTime(timeInSeconds, showHours));
  };

  const handleKeyDown = e => {
    if (e.key !== "Enter") return;
    formatAndSetTime();
  };

  const generateUrls = useCallback(
    (timeInSeconds, urlParams) => {
      const playbackUrls = recordingUrls.map(recordingUrl =>
        buildUrl(recordingUrl, {
          time: timeInSeconds,
          muted: urlParams.muted ? 1 : undefined,
          autoplay: urlParams.autoplay ? 1 : undefined,
        })
      );
      setShareLinks(playbackUrls);
    },
    [recordingUrls]
  );

  const handleRegenerateUrl = () => {
    let timeInSeconds = parseTime(rawInput);
    if (timeInSeconds > totalDuration) {
      timeInSeconds = totalDuration;
    }
    setCurrentPlaybackTime(timeInSeconds);
    generateUrls(timeInSeconds, params);
  };

  useEffect(() => {
    if (not(isOpen)) return;

    const initialTime = currentTimestamp > 0 ? currentTimestamp : 0;
    const initialParams = { muted: false, autoplay: false };

    setCurrentPlaybackTime(initialTime);
    setRawInput(formatTime(initialTime, showHours));
    setParams(initialParams);
    generateUrls(initialTime, initialParams);
  }, [isOpen, showHours, currentTimestamp, generateUrls]);

  return (
    <div className="flex w-full flex-col gap-8">
      <div className="neeto-ui-border-gray-200 flex flex-col gap-8 border-b px-6 pb-8">
        <div className="flex w-full flex-col gap-6">
          <div className="flex flex-col gap-y-2">
            <Typography style="h4">
              {t("neetoMolecules.shareRecording.configureUrlParameters")}
            </Typography>
            <Typography style="body3">
              {t(
                "neetoMolecules.shareRecording.configureUrlParametersDescription"
              )}
            </Typography>
          </div>
          <div className="flex items-center gap-x-3">
            <Label>{t("neetoMolecules.shareRecording.startVideoAt")}</Label>
            <Input
              unlimitedChars
              className="w-full max-w-32"
              data-testid="time-input"
              placeholder={showHours ? "HH:MM:SS" : "MM:SS"}
              value={rawInput}
              onBlur={formatAndSetTime}
              onChange={handlePlaybackTimestampChange}
              onKeyDown={handleKeyDown}
            />
          </div>
          {enableAutoplayAndMuteFlags && (
            <div className="flex flex-col gap-4">
              <Switch
                checked={muted}
                data-testid="muted-checkbox"
                id="muted"
                label={t("neetoMolecules.shareRecording.muted")}
                name="muted"
                onChange={e => handleFlagsChange("muted", e.target.checked)}
              />
              <Switch
                checked={autoplay}
                data-testid="autoplay-checkbox"
                id="autoplay"
                label={t("neetoMolecules.shareRecording.autoplay")}
                name="autoplay"
                onChange={e => handleFlagsChange("autoplay", e.target.checked)}
              />
            </div>
          )}
          <div className="mt-2 flex items-center gap-x-3">
            <Button
              data-testid="regenerate-url-button"
              label={t("neetoMolecules.shareRecording.regenerateUrl")}
              size="small"
              onClick={handleRegenerateUrl}
            />
            <Button
              data-testid="cancel-button"
              label={t("neetoMolecules.shareRecording.cancel")}
              size="small"
              style="text"
              onClick={onClose}
            />
          </div>
        </div>
      </div>
      <div className="flex flex-col gap-6 px-6">
        <div className="flex flex-col gap-y-2">
          <Typography style="h4">
            {t("neetoMolecules.shareRecording.generatedUrls")}
          </Typography>
          <div className="flex flex-col gap-y-3">
            {shareLinks.map((shareLink, index) => (
              <div
                className="neeto-ui-bg-gray-100 neeto-ui-rounded flex w-full items-start justify-between gap-2 p-2"
                key={index}
              >
                <Typography
                  className="min-w-0 flex-1 break-words"
                  style="body2"
                >
                  {shareLink}
                </Typography>
                <CopyToClipboardButton
                  className="flex-shrink-0"
                  size="small"
                  style="text"
                  value={shareLink}
                />
              </div>
            ))}
          </div>
        </div>
        {Preview && (
          <div className="flex flex-col gap-y-2">
            <Typography style="h4">
              {t("neetoMolecules.shareRecording.preview")}
            </Typography>
            <Preview {...{ currentPlaybackTime }} />
          </div>
        )}
      </div>
    </div>
  );
};

export default SharePaneBody;
